/** Phase 10D: Date and formatting helpers for renewals */

export function daysUntil(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
}

export function getRenewalWindow(daysLeft) {
  if (daysLeft == null || daysLeft < 0) return null;
  if (daysLeft <= 7) return '7_day';
  if (daysLeft <= 30) return '30_day';
  if (daysLeft <= 60) return '60_day';
  if (daysLeft <= 90) return '90_day';
  return null;
}

export function formatRenewalDate(date, locale = 'en') {
  if (!date) return '—';
  const d = new Date(date);
  return locale === 'ar'
    ? d.toLocaleDateString('ar-LB', { day: 'numeric', month: 'long', year: 'numeric' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatCurrency(amount, currency = 'USD', locale = 'en') {
  if (amount == null) return '—';
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-LB' : 'en-US', { style: 'currency', currency }).format(amount);
}

export function countdownText(daysLeft) {
  if (daysLeft == null || daysLeft < 0) return null;
  if (daysLeft === 0) return 'Expires today';
  if (daysLeft === 1) return '1 day left';
  return `${daysLeft} days left`;
}

/** Relative time for "last checked" (e.g. "2 days ago", "Just now") */
export function relativeTime(dateOrIso, locale = 'en') {
  if (!dateOrIso) return null;
  const d = new Date(dateOrIso);
  const now = new Date();
  const diffMs = now - d;
  const diffM = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffM < 1) return locale === 'ar' ? 'الآن' : 'Just now';
  if (diffM < 60) return locale === 'ar' ? `منذ ${diffM} د` : `${diffM} min ago`;
  if (diffH < 24) return locale === 'ar' ? `منذ ${diffH} س` : `${diffH} hours ago`;
  if (diffD === 1) return locale === 'ar' ? 'أمس' : 'Yesterday';
  if (diffD < 7) return locale === 'ar' ? `منذ ${diffD} أيام` : `${diffD} days ago`;
  if (diffD < 30) return locale === 'ar' ? `منذ ${Math.floor(diffD / 7)} أسابيع` : `${Math.floor(diffD / 7)} weeks ago`;
  return d.toLocaleDateString(locale === 'ar' ? 'ar-LB' : 'en-US', { month: 'short', day: 'numeric' });
}

/** Urgency level for styling (critical | urgent | high | medium | low | none) */
export function getUrgencyLevel(days) {
  if (days == null || days < 0) return 'expired';
  if (days <= 0) return 'expired';
  if (days <= 7) return 'critical';
  if (days <= 15) return 'urgent';
  if (days <= 30) return 'high';
  if (days <= 60) return 'medium';
  if (days <= 90) return 'low';
  return 'none';
}

/** Sort renewals: most urgent first, then by potential savings descending */
export function sortRenewals(renewals, daysUntilFn = daysUntil) {
  return [...(renewals || [])].sort((a, b) => {
    const daysA = daysUntilFn(a.end_date) ?? 999;
    const daysB = daysUntilFn(b.end_date) ?? 999;
    const daysDiff = daysA - daysB;
    if (daysDiff !== 0) return daysDiff;
    const saveA = a.potential_savings ?? a.potentialSavings ?? 0;
    const saveB = b.potential_savings ?? b.potentialSavings ?? 0;
    return saveB - saveA;
  });
}

/** Show only last 4 digits of policy number (security) */
export function maskPolicyNumber(policyNumber) {
  if (!policyNumber || typeof policyNumber !== 'string') return '—';
  const s = String(policyNumber).trim();
  if (s.length <= 4) return '****';
  return `••••${s.slice(-4)}`;
}

/**
 * Transform premium history from API into chart-ready data (for Chart.js).
 * @param {Array} premiumHistory - [{ effective_date, premium_amount }, ...]
 * @param {string} locale - 'en' | 'ar'
 * @param {number} maxMonths - cap at N months (default 24)
 */
export function generateChartData(premiumHistory, locale = 'en', maxMonths = 24) {
  const list = Array.isArray(premiumHistory) ? premiumHistory.slice(0, maxMonths) : [];
  return {
    labels: list.map((h) => new Date(h.effective_date).toLocaleDateString(locale === 'ar' ? 'ar-LB' : 'en-US', { month: 'short', year: 'numeric' })),
    datasets: [{
      label: 'Premium',
      data: list.map((h) => h.premium_amount),
      borderColor: '#1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      fill: true,
      tension: 0.4,
    }],
  };
}

